import React, { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';

// Shared UI Library Components
import {
  CustomIcon,
  DropdownElement,
  EIconName,
  Loader,
  useDebounce
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './service-revenue-refunds.scss';

// Components
import { LineChart } from '../../../charts';
import { MonthSlider } from '../../month-slider/month-slider';

// Store entities
import { fetchChartsInfo, fetchRevenueCounsellors } from '../../../../store/dashboard/dashboard.thunks';
import { getDashboardState } from '../../../../store/dashboard/dashboard.selectors';
import { getCompanyState } from '../../../../store/company/company.selectors';
import { getCurrencyState } from '../../../../store/currency/currency.selectors';

// Models
import { IChartLines } from '../../../../interfaces/charts.interfaces';

type TServiceRevenueRefundsProps = {};

export const ServiceRevenueRefunds: FC<TServiceRevenueRefundsProps> = (
  {}: TServiceRevenueRefundsProps
) => {

  const [hideBlock, setHideBlock] = useState<boolean>(false);
  const [searchCounsellor, setSearchCounsellor] = useState<string>('');
  const [activeCounsellors, setActiveCounsellors] = useState<'all' | number>('all');
  const [activeDate, setActiveDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const { charts, counsellors, totalCounsellors, loading, loadingCounsellors } = useSelector(getDashboardState);
  const { company } = useSelector(getCompanyState);
  const { currentCurrency } = useSelector(getCurrencyState);

  const dispatch = useDispatch<any>();

  const debouncedSearch = useDebounce(searchCounsellor, 1000);

  const dropdownOptions: { value: string, label: string }[] = counsellors.map(({ id, first_name, last_name }) => ({
    value: `${ id }`,
    label: `${ first_name } ${ last_name }`
  }));

  const generateRevenueDataBaseOnMonth = (): number[] => {
    if (!charts?.service_revenue?.length) return Array(dayjs(activeDate).daysInMonth()).fill(null);
    const daysInMonth = dayjs(activeDate).daysInMonth();
    let startDay = 0;
    let endDay = daysInMonth;

    if (charts?.service_revenue?.[0]?.created_at) {
      startDay = dayjs(charts?.service_revenue?.[0].created_at).date() - 1;
    }
    if (charts?.service_revenue?.[charts?.service_revenue.length - 1]?.created_at) {
      endDay = dayjs(charts?.service_revenue?.[charts?.service_revenue.length - 1].created_at).date() - 1;
    }

    const revenueData = charts?.service_revenue.map((revenue) => revenue.sum);

    return [
      ...new Array(startDay).fill(null),
      ...revenueData,
      ...new Array(daysInMonth - endDay).fill(null),
    ];
  }

  const chartLabels: string[] = new Array(dayjs(activeDate).daysInMonth()).fill(null).map((_, i) => `${ i + 1 }`);
  const chartLines: IChartLines[] = useMemo(() => {
    return ([
      {
        label: 'Service Revenue',
        data: generateRevenueDataBaseOnMonth()
      },
      // Add more chart lines if needed.
    ])
  }, [charts?.service_revenue]);

  const toggleHideBlockState = (): void => {
    setHideBlock(!hideBlock);
  }

  const loadChartsInfo = (filters?: { date?: string, worker_id?: number | 'all' }): void => {
    const worker_id = filters?.worker_id || activeCounsellors;
    const date = filters?.date || activeDate;

    dispatch(fetchChartsInfo({
      ...(date && { date }),
      ...((worker_id !== 'all' && worker_id !== 0) && { worker_id })
    }));
  }

  const onMonthChangeHandler = (date: string): void => {
    setActiveDate(date);
    loadChartsInfo({ date });
  }

  const loadRevenueCounsellors = async (isFirstLoad: boolean = true, isSearchLoading: boolean = false): Promise<void> => {
    dispatch(fetchRevenueCounsellors({
      filters: {
        search: debouncedSearch || '',
        offset: isSearchLoading ? 0 : counsellors.length,
      },
      isFirstLoad: isSearchLoading ? true : isFirstLoad
    }));
  }

  const onSearchCounsellor = (value: string): void => {
    setSearchCounsellor(value);
  }

  const onCounsellorChangeHandler = (id: string): void => {
    setActiveCounsellors(+id);
    loadChartsInfo({ worker_id: +id === 0 ? 'all' : +id });
  }

  const onPopupScrollHandler = (event: any): void => {
    const { scrollTop, scrollHeight, offsetHeight } = event.target as HTMLDivElement;
    if ((Math.abs(scrollTop) + offsetHeight + 50 < scrollHeight) || loadingCounsellors || counsellors?.length >= (totalCounsellors || 0)) return;
    loadRevenueCounsellors(false);
  }

  useEffect(() => {
    loadRevenueCounsellors(true, true);
  }, [debouncedSearch]);

  useEffect(() => {
    loadRevenueCounsellors();
  }, []);

  return (
    <div className="service-revenue-refunds">
      <div className="service-revenue-refunds--header">
        <div className="header-title">
          Service Revenue
          {/*Service Revenue & Refunds*/ }
        </div>
        <div className={ `header-action header-action--${ hideBlock ? 'open' : 'hide' }` }
             onClick={ toggleHideBlockState }>
          { hideBlock ? 'Show' : 'Hide' } <CustomIcon size={ 'size-md' } name={ EIconName.ArrowDown }/>
        </div>
      </div>

      <div className="service-revenue-refunds--filters" hidden={ hideBlock }>
        <div className="filters-item">
          <DropdownElement defaultValue={ '' }
                           allOption={ { label: 'All Consultants', value: '' } }
                           options={ dropdownOptions }
                           showSearch={ true }
                           placeholder={ 'Consultant' }
                           suffixIcon={ loadingCounsellors ? <Loader size={ 'small' }/> :
                             <CustomIcon name={ EIconName.ArrowDown }/> }
                           onSearch={ onSearchCounsellor }
                           onChange={ onCounsellorChangeHandler }
                           onPopupScroll={ onPopupScrollHandler }
          />
        </div>
        <div className="filters-item">
          { !!company && <MonthSlider companyCreated={ company?.created_at } changeDate={ onMonthChangeHandler }/> }
        </div>
      </div>

      { loading && <div className="service-revenue-refunds--loader">
        <Loader size={ 'large' }/>
      </div> }

      <div className="service-revenue-refunds--content" hidden={ hideBlock }>
        { currentCurrency && <LineChart labels={ chartLabels } lines={ chartLines } currency={ currentCurrency }
                                        emptyData={ !charts?.service_revenue?.length }/> }
      </div>
    </div>
  );
}
